import { EllipsisVerticalIcon } from "lucide-react";
import { useLayoutEffect, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { inkOn } from "./canvas/presenceTracks";
import { barView, type ChangeSet, fieldLine } from "./workflowChanges";

const KIND_WORDS = { added: "New", edited: "Edited", removed: "Removed" } as const;

const BAR =
  "pointer-events-auto flex h-9 items-center gap-1 rounded-lg border border-(--workflow-change)/35 bg-canvas-popover pr-1 pl-3 text-[13px] leading-5 font-medium whitespace-nowrap shadow-canvas-popover backdrop-blur-[12px] motion-safe:animate-canvas-rise-in";

function Dot({ live }: { readonly live: boolean }) {
  return (
    <span aria-hidden className="relative mr-1 flex size-2 shrink-0">
      {live ? <span className="absolute inset-0 rounded-full bg-(--workflow-change) opacity-60 motion-safe:animate-ping" /> : null}
      <span className="relative size-2 rounded-full bg-(--workflow-change)" />
    </span>
  );
}

const chartColumn = (bar: HTMLElement | null) => bar?.closest<HTMLElement>("[data-workflow-chart-column]") ?? null;

/** The bar over the chart for each set of changes: eve at work, then what she or a colleague changed. */
export function ChangeBar(props: BarActions & { readonly sets: ReadonlyArray<ChangeSet> }) {
  const { sets, ...actions } = props;
  return sets.map((set) => <Bar key={set.key} set={set} {...actions} />);
}

interface BarActions {
  readonly canUndo: boolean;
  /** Whether a step is on the chart to be shown. */
  readonly onChart: (step: string) => boolean;
  readonly onUndo: (group: string) => void;
  readonly onShow: (steps: ReadonlyArray<string>) => void;
  readonly onDismiss: (set: ChangeSet) => void;
  readonly onFocusLeaving: () => void;
  /** Opens Ask eve on a chat; with `approve`, focused on its waiting card's Approve. */
  readonly onOpenChat: (threadId: string | null, focus: "composer" | "approve") => void;
}

function Face({ name, color }: { readonly name: string; readonly color: string }) {
  return (
    <span
      title={`For ${name}`}
      aria-label={`For ${name}`}
      role="img"
      style={{ backgroundColor: color, color: inkOn(color) }}
      className="mr-1 flex size-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
      data-workflow-change-for={name}
    >
      {name.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

function Bar({ set, canUndo, onChart, onUndo, onShow, onDismiss, onFocusLeaving, onOpenChat }: BarActions & { readonly set: ChangeSet }) {
  const ref = useRef<HTMLDivElement>(null);
  /** A menu item was chosen: it runs once its menu has closed and given focus back. */
  const chosen = useRef<"dismiss" | "open-chat" | null>(null);
  const leaving = useRef(onFocusLeaving);
  useLayoutEffect(() => {
    leaving.current = onFocusLeaving;
  });
  // Runs before React takes the bar out of the page, while it still holds focus.
  useLayoutEffect(() => {
    const node = ref.current;
    return () => {
      if (node?.contains(document.activeElement)) leaving.current();
    };
  }, []);

  const view = barView(set, canUndo);
  const working = view.state === "editing" || view.state === "waiting";
  const shown = set.steps.flatMap((step) => (step.kind !== "removed" && onChart(step.id) ? [step.id] : []));
  const group = set.author.kind === "eve" ? set.author.group : null;
  const threadId = set.author.kind === "eve" ? set.author.threadId : null;

  return (
    <div ref={ref} className={BAR} data-workflow-change-bar={view.state}>
      <span role="status" className="sr-only">
        {view.title}
      </span>
      {working || view.face ? <Dot live={view.live} /> : null}
      {view.face ? <Face {...view.face} /> : null}
      {working ? (
        <>
          <span>{view.title}</span>
          {view.count ? <span className="mr-2 ml-1.5 text-muted-foreground tabular-nums">{view.count}</span> : <span className="mr-2" />}
        </>
      ) : shown.length > 0 ? (
        <Button
          size="sm"
          variant="ghost"
          className="-ml-2 text-(--workflow-change) hover:text-(--workflow-change)"
          onClick={() => onShow(shown)}
          data-workflow-change-show=""
        >
          {view.title}
        </Button>
      ) : (
        <span className="mr-2 text-(--workflow-change)">{view.title}</span>
      )}
      {view.review ? (
        <Button size="sm" onClick={() => onOpenChat(threadId, "approve")} data-workflow-change-review="">
          Review
        </Button>
      ) : null}
      {view.details ? <Details set={set} onChart={onChart} onShow={onShow} /> : null}
      {view.undo && group ? (
        <Button size="sm" onClick={() => onUndo(group)} data-workflow-change-undo="">
          Undo
        </Button>
      ) : null}
      {view.menu ? (
        <Menu
          onOpenChangeComplete={(open) => {
            const item = chosen.current;
            if (open || item === null) return;
            chosen.current = null;
            if (item === "open-chat") {
              onOpenChat(view.openChat, "composer");
              return;
            }
            chartColumn(ref.current)?.focus({ preventScroll: true });
            onDismiss(set);
          }}
        >
          <MenuTrigger render={<Button size="icon-sm" variant="ghost" aria-label="More" data-workflow-change-menu="" />}>
            <EllipsisVerticalIcon />
          </MenuTrigger>
          <MenuPopup align="end">
            {view.openChat ? (
              <MenuItem
                onClick={() => {
                  chosen.current = "open-chat";
                }}
                data-workflow-change-open-chat=""
              >
                Open chat
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                chosen.current = "dismiss";
              }}
              data-workflow-change-dismiss=""
            >
              Dismiss
            </MenuItem>
          </MenuPopup>
        </Menu>
      ) : null}
    </div>
  );
}

function Details({
  set,
  onChart,
  onShow,
}: {
  readonly set: ChangeSet;
  readonly onChart: (step: string) => boolean;
  readonly onShow: (steps: ReadonlyArray<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Button size="sm" variant="outline" data-workflow-change-details="" />}>Details</PopoverTrigger>
      <PopoverPopup className="w-84" viewportClassName="py-1.5 [--viewport-inline-padding:--spacing(1.5)]" data-workflow-change-list="">
        <ul className="flex max-h-80 flex-col">
          {set.steps.map((step) => {
            const label = step.after?.label ?? step.before?.label ?? "";
            const reveal = step.kind !== "removed" && onChart(step.id);
            const fields = step.kind === "edited" ? (step.fields.length > 0 ? step.fields.map(fieldLine) : [{ noun: "links", values: null }]) : [];
            const body = (
              <>
                <span className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{label}</span>
                  <span className={cn("shrink-0 text-[12px]", step.kind === "removed" ? "text-muted-foreground" : "text-(--workflow-change)")}>{KIND_WORDS[step.kind]}</span>
                </span>
                {fields.map(({ noun, values }) => (
                  <span key={noun} className="flex min-w-0 flex-col gap-0.5 text-[12px] leading-4 text-muted-foreground">
                    <span>{noun}</span>
                    {values ? (
                      <span className="min-w-0 break-words">
                        <span className="line-through decoration-foreground/30">{values.before}</span>
                        <span aria-label="to"> → </span>
                        <span className="text-foreground">{values.after}</span>
                      </span>
                    ) : null}
                  </span>
                ))}
              </>
            );
            const row = "flex w-full flex-col gap-1 rounded-md px-2.5 py-2 text-left text-[13px] leading-5";
            return (
              <li key={step.id}>
                {reveal ? (
                  <button
                    type="button"
                    className={cn(row, "outline-none hover:bg-accent focus-visible:bg-accent")}
                    onClick={() => {
                      setOpen(false);
                      onShow([step.id]);
                    }}
                    data-workflow-change-row={step.id}
                  >
                    {body}
                  </button>
                ) : (
                  <div className={row} data-workflow-change-row={step.id}>
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </PopoverPopup>
    </Popover>
  );
}
